import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { markInvoicePaid, setInvoiceStatus, watchInvoices } from '../lib/data.js'
import { sendInvoiceEmail } from '../lib/stripe.js'
import { fmtDate, fmtDateTime, fmtMoney, statusPillClass } from '../lib/format.js'

export default function Invoices() {
  const [invoices, setInvoices] = useState({})
  const [statusFilter, setStatusFilter] = useState('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [flash, setFlash] = useState(null)

  useEffect(() => watchInvoices(setInvoices), [])

  const list = useMemo(() => {
    const all = Object.entries(invoices).map(([id, v]) => ({ id, ...v }))
    return all.filter((inv) => {
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false
      if (from && inv.dueDate && inv.dueDate < from) return false
      if (to && inv.dueDate && inv.dueDate > to) return false
      return true
    }).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  }, [invoices, statusFilter, from, to])

  const onMarkPaid = async (inv) => {
    if (!confirm(`Mark invoice #${inv.invoiceNumber} (${fmtMoney(inv.amount)}) as paid?`)) return
    await markInvoicePaid(inv.id, { paidVia: 'manual' })
  }
  const onMarkOpen = async (inv) => {
    await setInvoiceStatus(inv.id, 'sent', { paidAt: null })
  }
  const onSend = async (inv) => {
    setFlash(null)
    try {
      await sendInvoiceEmail(inv.id)
      setFlash({ kind: 'ok', msg: `Reminder queued for #${inv.invoiceNumber}.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Send failed.' })
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-extrabold">Invoices</h1>

      <div className="card">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="label">Status</label>
            <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label className="label">From</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="flex items-end">
            <button className="btn-secondary w-full" onClick={() => { setStatusFilter('all'); setFrom(''); setTo('') }}>
              Clear filters
            </button>
          </div>
        </div>
      </div>

      {flash ? (
        <div className={`rounded-lg border px-3 py-2 text-sm ${flash.kind === 'ok' ? 'bg-brand-mint border-brand-green text-brand-green-deep' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {flash.msg}
        </div>
      ) : null}

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2">Invoice #</th>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Due</th>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No invoices match these filters.</td></tr>
              ) : list.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-extrabold">#{inv.invoiceNumber}</td>
                  <td className="px-4 py-3">
                    {inv.customerId ? (
                      <Link to={`/customers/${inv.customerId}`} className="hover:underline">{inv.customerName || '—'}</Link>
                    ) : (inv.customerName || '—')}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">{fmtMoney(inv.amount)}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(inv.dueDate)}</td>
                  <td className="px-4 py-3 text-xs">{inv.billingPeriod || '—'}</td>
                  <td className="px-4 py-3"><span className={statusPillClass(inv.status)}>{inv.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === 'paid' ? (
                      <button className="text-xs text-gray-500 hover:underline" onClick={() => onMarkOpen(inv)}>Reopen</button>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <button className="text-xs text-brand-green-deep font-bold hover:underline" onClick={() => onSend(inv)}>Send reminder</button>
                        <button className="text-xs font-bold text-gray-700 hover:underline" onClick={() => onMarkPaid(inv)}>Mark paid</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
